import { useMemo, useState } from 'react';
import JSZip from 'jszip';
import { FileDown, Calendar, FileText, Receipt, Package } from 'lucide-react';
import { Card, PageTitle } from '../ui';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCrm } from '../store';
import { formatEUR } from '../utils';
import { toast } from 'sonner';
import type { Facture } from '../types';

function calcTotaux(f: Facture) {
  const totalHT = f.lignes.reduce((s, l) => s + (l.quantite || 0) * (l.prixUnitaireHT || 0), 0);
  const remise = totalHT * ((f.remisePct || 0) / 100);
  const htNet = totalHT - remise;
  const tva = f.lignes.reduce((s, l) => {
    const lht = (l.quantite || 0) * (l.prixUnitaireHT || 0);
    const lhtNet = lht * (1 - (f.remisePct || 0) / 100);
    return s + lhtNet * ((l.tvaPct || 0) / 100);
  }, 0);
  const ttc = htNet + tva;
  return { totalHT, htNet, tva, ttc };
}

function downloadFile(content: string, filename: string, mime: string) {
  const blob = new Blob(['\ufeff' + content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const startOfYear = () => `${new Date().getFullYear()}-01-01`;

export default function ExportComptable() {
  const data = useCrm();
  const [dateDebut, setDateDebut] = useState(startOfMonth());
  const [dateFin, setDateFin] = useState(today());
  const [format, setFormat] = useState<'csv' | 'fec' | 'sage' | 'pack'>('csv');
  const [statutFiltre, setStatutFiltre] = useState<'all' | 'payees' | 'emises'>('all');

  const factures = useMemo(() => {
    return data.factures
      .filter((f) => f.type === 'Facture')
      .filter((f) => f.dateEmission >= dateDebut && f.dateEmission <= dateFin)
      .filter((f) => {
        if (statutFiltre === 'all') return true;
        if (statutFiltre === 'payees') return f.statut === 'Payée';
        return ['Émise', 'Envoyée', 'Partiellement payée', 'En retard'].includes(f.statut);
      })
      .sort((a, b) => a.dateEmission.localeCompare(b.dateEmission));
  }, [data.factures, dateDebut, dateFin, statutFiltre]);

  const totaux = useMemo(() => {
    return factures.reduce(
      (acc, f) => {
        const t = calcTotaux(f);
        acc.ht += t.htNet;
        acc.tva += t.tva;
        acc.ttc += t.ttc;
        return acc;
      },
      { ht: 0, tva: 0, ttc: 0 }
    );
  }, [factures]);

  const setPeriod = (p: 'mois' | 'trim' | 'annee' | 'preced_annee') => {
    const now = new Date();
    if (p === 'mois') {
      setDateDebut(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
      setDateFin(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10));
    } else if (p === 'trim') {
      const q = Math.floor(now.getMonth() / 3);
      setDateDebut(new Date(now.getFullYear(), q * 3, 1).toISOString().slice(0, 10));
      setDateFin(new Date(now.getFullYear(), q * 3 + 3, 0).toISOString().slice(0, 10));
    } else if (p === 'annee') {
      setDateDebut(`${now.getFullYear()}-01-01`);
      setDateFin(`${now.getFullYear()}-12-31`);
    } else {
      setDateDebut(`${now.getFullYear() - 1}-01-01`);
      setDateFin(`${now.getFullYear() - 1}-12-31`);
    }
  };

  const exportCSV = () => {
    if (factures.length === 0) {
      toast.error('Aucune facture sur cette période');
      return;
    }
    const rows = [
      ['N° Facture', 'Date émission', 'Date échéance', 'Date paiement', 'Client', 'SIRET', 'TVA Intra', 'Total HT', 'TVA', 'Total TTC', 'Montant payé', 'Reste dû', 'Statut', 'Mode paiement', 'Notes'],
    ];
    factures.forEach((f) => {
      const t = calcTotaux(f);
      const restant = Math.max(0, t.ttc - (f.montantPaye || 0) - (f.acompteVerse || 0));
      rows.push([
        f.numero,
        f.dateEmission,
        f.dateEcheance || '',
        f.datePaiement || '',
        f.clientNom,
        f.clientSiret || '',
        f.clientTvaIntra || '',
        t.htNet.toFixed(2),
        t.tva.toFixed(2),
        t.ttc.toFixed(2),
        ((f.montantPaye || 0) + (f.acompteVerse || 0)).toFixed(2),
        restant.toFixed(2),
        f.statut,
        f.modePaiement || '',
        (f.notes || '').replace(/[\r\n;]/g, ' '),
      ]);
    });
    rows.push([]);
    rows.push(['', '', '', '', '', '', 'TOTAUX', totaux.ht.toFixed(2), totaux.tva.toFixed(2), totaux.ttc.toFixed(2), '', '', '', '', '']);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    downloadFile(csv, `export-comptable_${dateDebut}_${dateFin}.csv`, 'text/csv');
    toast.success(`${factures.length} factures exportées`);
  };

  const exportFEC = () => {
    if (factures.length === 0) {
      toast.error('Aucune facture sur cette période');
      return;
    }
    // Format FEC officiel — Fichier des Écritures Comptables
    // 18 colonnes obligatoires séparées par tabulation
    const headers = [
      'JournalCode', 'JournalLib', 'EcritureNum', 'EcritureDate', 'CompteNum', 'CompteLib',
      'CompAuxNum', 'CompAuxLib', 'PieceRef', 'PieceDate', 'EcritureLib',
      'Debit', 'Credit', 'EcritureLet', 'DateLet', 'ValidDate', 'Montantdevise', 'Idevise',
    ];
    const lines: string[][] = [headers];
    let ecritureNum = 1;

    factures.forEach((f) => {
      const t = calcTotaux(f);
      const dateNum = f.dateEmission.replace(/-/g, '');
      const num = String(ecritureNum++).padStart(6, '0');
      const lib = `Facture ${f.numero} - ${f.clientNom}`.slice(0, 200);
      const compteClient = `411${(f.clientNom || 'CLI').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5).padEnd(5, '0')}`;

      // Débit client TTC
      lines.push([
        'VE', 'Ventes', num, dateNum, compteClient, f.clientNom.slice(0, 60),
        compteClient, f.clientNom.slice(0, 60), f.numero, dateNum, lib,
        t.ttc.toFixed(2).replace('.', ','), '0,00', '', '', dateNum, '', '',
      ]);
      // Crédit ventes HT
      lines.push([
        'VE', 'Ventes', num, dateNum, '706000', 'Prestations de services',
        '', '', f.numero, dateNum, lib,
        '0,00', t.htNet.toFixed(2).replace('.', ','), '', '', dateNum, '', '',
      ]);
      // Crédit TVA collectée
      if (t.tva > 0) {
        lines.push([
          'VE', 'Ventes', num, dateNum, '445710', 'TVA collectée 20%',
          '', '', f.numero, dateNum, lib,
          '0,00', t.tva.toFixed(2).replace('.', ','), '', '', dateNum, '', '',
        ]);
      }
    });

    const fec = lines.map((r) => r.join('\t')).join('\r\n');
    const periode = `${dateDebut.replace(/-/g, '')}_${dateFin.replace(/-/g, '')}`;
    downloadFile(fec, `FEC_${periode}.txt`, 'text/plain');
    toast.success('FEC généré');
  };

  const exportSage = () => {
    if (factures.length === 0) {
      toast.error('Aucune facture sur cette période');
      return;
    }
    // Format Sage / Ciel / EBP - CSV simplifié écritures
    const rows = [['Date', 'Journal', 'N° Pièce', 'Compte', 'Libellé', 'Débit', 'Crédit']];
    factures.forEach((f) => {
      const t = calcTotaux(f);
      const compteClient = `411${(f.clientNom || 'CLI').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5).padEnd(5, '0')}`;
      const lib = `Facture ${f.numero}`;
      rows.push([f.dateEmission, 'VE', f.numero, compteClient, f.clientNom, t.ttc.toFixed(2), '0.00']);
      rows.push([f.dateEmission, 'VE', f.numero, '706000', lib, '0.00', t.htNet.toFixed(2)]);
      if (t.tva > 0) {
        rows.push([f.dateEmission, 'VE', f.numero, '445710', 'TVA 20%', '0.00', t.tva.toFixed(2)]);
      }
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    downloadFile(csv, `export-sage_${dateDebut}_${dateFin}.csv`, 'text/csv');
    toast.success('Export Sage/Ciel/EBP généré');
  };

  const exportPackAnnuel = async () => {
    if (factures.length === 0) {
      toast.error('Aucune facture sur cette période');
      return;
    }
    const zip = new JSZip();
    const periode = `${dateDebut}_${dateFin}`;

    // 1. CSV simple
    const csvRows: string[][] = [
      ['N° Facture', 'Date émission', 'Date échéance', 'Date paiement', 'Client', 'SIRET', 'TVA Intra', 'Total HT', 'TVA', 'Total TTC', 'Montant payé', 'Reste dû', 'Statut', 'Mode paiement', 'Notes'],
    ];
    factures.forEach((f) => {
      const t = calcTotaux(f);
      const restant = Math.max(0, t.ttc - (f.montantPaye || 0) - (f.acompteVerse || 0));
      csvRows.push([
        f.numero, f.dateEmission, f.dateEcheance || '', f.datePaiement || '',
        f.clientNom, f.clientSiret || '', f.clientTvaIntra || '',
        t.htNet.toFixed(2), t.tva.toFixed(2), t.ttc.toFixed(2),
        ((f.montantPaye || 0) + (f.acompteVerse || 0)).toFixed(2), restant.toFixed(2),
        f.statut, f.modePaiement || '', (f.notes || '').replace(/[\r\n;]/g, ' '),
      ]);
    });
    csvRows.push([]);
    csvRows.push(['', '', '', '', '', '', 'TOTAUX', totaux.ht.toFixed(2), totaux.tva.toFixed(2), totaux.ttc.toFixed(2), '', '', '', '', '']);
    const csv = csvRows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    zip.file(`01_factures_${periode}.csv`, '\ufeff' + csv);

    // 2. FEC officiel
    const fecHeaders = ['JournalCode', 'JournalLib', 'EcritureNum', 'EcritureDate', 'CompteNum', 'CompteLib', 'CompAuxNum', 'CompAuxLib', 'PieceRef', 'PieceDate', 'EcritureLib', 'Debit', 'Credit', 'EcritureLet', 'DateLet', 'ValidDate', 'Montantdevise', 'Idevise'];
    const fecLines: string[][] = [fecHeaders];
    let n = 1;
    factures.forEach((f) => {
      const t = calcTotaux(f);
      const dateNum = f.dateEmission.replace(/-/g, '');
      const num = String(n++).padStart(6, '0');
      const lib = `Facture ${f.numero} - ${f.clientNom}`.slice(0, 200);
      const cpt = `411${(f.clientNom || 'CLI').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5).padEnd(5, '0')}`;
      fecLines.push(['VE', 'Ventes', num, dateNum, cpt, f.clientNom.slice(0, 60), cpt, f.clientNom.slice(0, 60), f.numero, dateNum, lib, t.ttc.toFixed(2).replace('.', ','), '0,00', '', '', dateNum, '', '']);
      fecLines.push(['VE', 'Ventes', num, dateNum, '706000', 'Prestations de services', '', '', f.numero, dateNum, lib, '0,00', t.htNet.toFixed(2).replace('.', ','), '', '', dateNum, '', '']);
      if (t.tva > 0) fecLines.push(['VE', 'Ventes', num, dateNum, '445710', 'TVA collectée 20%', '', '', f.numero, dateNum, lib, '0,00', t.tva.toFixed(2).replace('.', ','), '', '', dateNum, '', '']);
    });
    zip.file(`02_FEC_${periode.replace(/-/g, '')}.txt`, fecLines.map((r) => r.join('\t')).join('\r\n'));

    // 3. Sage / Ciel / EBP
    const sageRows: string[][] = [['Date', 'Journal', 'N° Pièce', 'Compte', 'Libellé', 'Débit', 'Crédit']];
    factures.forEach((f) => {
      const t = calcTotaux(f);
      const cpt = `411${(f.clientNom || 'CLI').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5).padEnd(5, '0')}`;
      sageRows.push([f.dateEmission, 'VE', f.numero, cpt, f.clientNom, t.ttc.toFixed(2), '0.00']);
      sageRows.push([f.dateEmission, 'VE', f.numero, '706000', `Facture ${f.numero}`, '0.00', t.htNet.toFixed(2)]);
      if (t.tva > 0) sageRows.push([f.dateEmission, 'VE', f.numero, '445710', 'TVA 20%', '0.00', t.tva.toFixed(2)]);
    });
    const sageCsv = sageRows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    zip.file(`03_sage_ciel_ebp_${periode}.csv`, '\ufeff' + sageCsv);

    // 4. Résumé texte
    const payees = factures.filter((f) => f.statut === 'Payée').length;
    const enRetard = factures.filter((f) => f.statut === 'En retard').length;
    const resume = [
      `═══════════════════════════════════════════════`,
      `  PACK ANNUEL COMPTABLE — Cygnature Production`,
      `═══════════════════════════════════════════════`,
      ``,
      `Période : du ${dateDebut} au ${dateFin}`,
      `Filtre statut : ${statutFiltre === 'all' ? 'Toutes' : statutFiltre === 'payees' ? 'Payées uniquement' : 'Émises (non payées)'}`,
      `Généré le : ${new Date().toLocaleString('fr-FR')}`,
      ``,
      `─── TOTAUX ───`,
      `Nombre de factures : ${factures.length}`,
      `  • Payées          : ${payees}`,
      `  • En retard       : ${enRetard}`,
      `  • Autres          : ${factures.length - payees - enRetard}`,
      ``,
      `Total HT  : ${totaux.ht.toFixed(2)} €`,
      `TVA       : ${totaux.tva.toFixed(2)} €`,
      `Total TTC : ${totaux.ttc.toFixed(2)} €`,
      ``,
      `─── FICHIERS INCLUS ───`,
      `  01_factures_*.csv     → Tableau Excel/Numbers`,
      `  02_FEC_*.txt          → Fichier des Écritures Comptables (légal — administration fiscale)`,
      `  03_sage_ciel_ebp_*.csv → Import logiciel comptable Sage / Ciel / EBP`,
      ``,
      `À transmettre à votre comptable.`,
    ].join('\r\n');
    zip.file('00_RESUME.txt', resume);

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pack-comptable_${periode}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Pack annuel téléchargé (${factures.length} factures, 4 fichiers)`);
  };

  const handleExport = () => {
    if (format === 'csv') exportCSV();
    else if (format === 'fec') exportFEC();
    else if (format === 'sage') exportSage();
    else exportPackAnnuel();
  };

  return (
    <div className="space-y-6">
      <PageTitle icon={<FileDown className="text-amber-400" />} title="Export comptable" subtitle="Exportez vos factures pour votre comptable (CSV, FEC, Sage/Ciel/EBP)" />

      <Card className="p-6 bg-slate-900 border-slate-800 space-y-5">
        <div>
          <Label className="text-slate-300 mb-2 block">Périodes rapides</Label>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setPeriod('mois')} className="bg-slate-950 border-slate-800 text-slate-200">Mois en cours</Button>
            <Button size="sm" variant="outline" onClick={() => setPeriod('trim')} className="bg-slate-950 border-slate-800 text-slate-200">Trimestre</Button>
            <Button size="sm" variant="outline" onClick={() => setPeriod('annee')} className="bg-slate-950 border-slate-800 text-slate-200">Année en cours</Button>
            <Button size="sm" variant="outline" onClick={() => setPeriod('preced_annee')} className="bg-slate-950 border-slate-800 text-slate-200">Année précédente</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="date-debut" className="text-slate-300 flex items-center gap-1.5"><Calendar size={14} /> Date début</Label>
            <Input id="date-debut" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} className="bg-slate-950 border-slate-800 text-slate-100 mt-1" />
          </div>
          <div>
            <Label htmlFor="date-fin" className="text-slate-300 flex items-center gap-1.5"><Calendar size={14} /> Date fin</Label>
            <Input id="date-fin" type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} className="bg-slate-950 border-slate-800 text-slate-100 mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">Statut</Label>
            <Select value={statutFiltre} onValueChange={(v: any) => setStatutFiltre(v)}>
              <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-100 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="payees">Payées uniquement</SelectItem>
                <SelectItem value="emises">Émises (non payées)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-slate-300 mb-2 block">Format d'export</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <button
              onClick={() => setFormat('csv')}
              className={`p-4 rounded-lg border text-left transition-colors ${format === 'csv' ? 'border-amber-500 bg-amber-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`}
            >
              <FileText className={format === 'csv' ? 'text-amber-400' : 'text-slate-400'} size={18} strokeWidth={1.5} />
              <div className="font-medium text-slate-100 mt-2">CSV simple</div>
              <div className="text-xs text-slate-500 mt-1">Excel / Numbers / Google Sheets — universel</div>
            </button>
            <button
              onClick={() => setFormat('fec')}
              className={`p-4 rounded-lg border text-left transition-colors ${format === 'fec' ? 'border-amber-500 bg-amber-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`}
            >
              <Receipt className={format === 'fec' ? 'text-amber-400' : 'text-slate-400'} size={18} strokeWidth={1.5} />
              <div className="font-medium text-slate-100 mt-2">FEC (légal)</div>
              <div className="text-xs text-slate-500 mt-1">Fichier des Écritures Comptables — exigé par l'administration fiscale</div>
            </button>
            <button
              onClick={() => setFormat('sage')}
              className={`p-4 rounded-lg border text-left transition-colors ${format === 'sage' ? 'border-amber-500 bg-amber-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`}
            >
              <FileDown className={format === 'sage' ? 'text-amber-400' : 'text-slate-400'} size={18} strokeWidth={1.5} />
              <div className="font-medium text-slate-100 mt-2">Sage / Ciel / EBP</div>
              <div className="text-xs text-slate-500 mt-1">Écritures comptables CSV importables</div>
            </button>
            <button
              onClick={() => setFormat('pack')}
              className={`p-4 rounded-lg border text-left transition-colors ${format === 'pack' ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`}
            >
              <Package className={format === 'pack' ? 'text-emerald-400' : 'text-slate-400'} size={18} strokeWidth={1.5} />
              <div className="font-medium text-slate-100 mt-2">Pack annuel ZIP <span className="text-[10px] text-emerald-400 ml-1">★</span></div>
              <div className="text-xs text-slate-500 mt-1">CSV + FEC + Sage + résumé en un seul ZIP — pour ton expert-comptable</div>
            </button>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-slate-900 border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-slate-100 font-medium">Aperçu de la période</h3>
            <p className="text-xs text-slate-500 mt-1">Du {dateDebut} au {dateFin}</p>
          </div>
          <Button onClick={handleExport} disabled={factures.length === 0} className="bg-amber-500 hover:bg-amber-400 text-slate-950">
            <FileDown size={16} className="mr-2" /> Exporter
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Factures</div>
            <div className="text-xl font-serif text-slate-100 mt-1">{factures.length}</div>
          </div>
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Total HT</div>
            <div className="text-xl font-serif text-slate-100 mt-1">{formatEUR(totaux.ht)}</div>
          </div>
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">TVA</div>
            <div className="text-xl font-serif text-slate-100 mt-1">{formatEUR(totaux.tva)}</div>
          </div>
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Total TTC</div>
            <div className="text-xl font-serif text-amber-400 mt-1">{formatEUR(totaux.ttc)}</div>
          </div>
        </div>
        {factures.length > 0 && (
          <div className="mt-4 max-h-64 overflow-y-auto border border-slate-800 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-950 sticky top-0">
                <tr className="text-slate-400">
                  <th className="text-left p-2">N°</th>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Client</th>
                  <th className="text-right p-2">HT</th>
                  <th className="text-right p-2">TTC</th>
                  <th className="text-left p-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {factures.map((f) => {
                  const t = calcTotaux(f);
                  return (
                    <tr key={f.id} className="border-t border-slate-800 text-slate-300">
                      <td className="p-2 font-mono">{f.numero}</td>
                      <td className="p-2">{f.dateEmission}</td>
                      <td className="p-2 truncate max-w-[200px]">{f.clientNom}</td>
                      <td className="p-2 text-right">{formatEUR(t.htNet)}</td>
                      <td className="p-2 text-right">{formatEUR(t.ttc)}</td>
                      <td className="p-2"><span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800">{f.statut}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
